/**
 * Airport lookup and distance calculation for FVrad
 * Uses OpenFlights airports database for IATA/ICAO lookup
 */

import airportsData from '../assets/data/airports.json';

export interface Airport {
  iata: string | null;
  icao: string | null;
  name: string;
  city: string;
  country: string;
  lat: number;
  lon: number;
}

const airports = airportsData as Airport[];

/** Earth radius in km for Haversine formula */
const EARTH_RADIUS_KM = 6371;

/**
 * Find airport by IATA code (case-insensitive).
 * Returns null if not found or code is empty.
 */
export function findAirport(code: string): Airport | null {
  if (!code || typeof code !== 'string') return null;
  const normalized = code.trim().toUpperCase();
  if (!normalized) return null;
  return (
    airports.find((a) => a.iata && a.iata.toUpperCase() === normalized) ?? null
  );
}

/**
 * Search airports by city name (case-insensitive, partial match).
 * Prefers airports with "International" in the name.
 * Returns the best match or null.
 */
export function findAirportByCity(city: string): Airport | null {
  if (!city || typeof city !== 'string') return null;
  const term = city.trim().toLowerCase();
  if (!term) return null;
  const matches = airports.filter(
    (a) =>
      a.iata &&
      (a.city.toLowerCase().includes(term) ||
        term.includes(a.city.toLowerCase()) ||
        a.name.toLowerCase().includes(term))
  );
  if (matches.length === 0) return null;
  // Prefer International airports
  const international = matches.find((a) =>
    a.name.toLowerCase().includes('international')
  );
  return international ?? matches[0];
}

export interface ParsedItinerary {
  origin: Airport;
  dest: Airport;
  originIata: string;
  destIata: string;
  durationMinutes: number;
}

/**
 * Parse flight itinerary string into origin/destination airports and duration.
 * Supports: "JFK to LHR", "JFK-LHR", "New York to London", "JFK London"
 * @returns Parsed itinerary or null if invalid
 */
export function parseItinerary(itinerary: string): ParsedItinerary | null {
  if (!itinerary || typeof itinerary !== 'string') return null;
  const raw = itinerary.trim();
  if (!raw) return null;

  // Split by common separators: " to ", " - ", " – ", " → ", ","
  let parts = raw
    .split(/\s+to\s+|\s*-\s*|\s*–\s*|\s*—\s*|\s*→\s*|,\s*/i)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    // Try "JFK LHR" - two 3-letter codes separated by space
    const tokens = raw.split(/\s+/).filter(Boolean);
    if (tokens.length === 2 && tokens[0].length === 3 && tokens[1].length === 3) {
      parts = [tokens[0], tokens[1]];
    }
  }

  if (parts.length < 2) return null;

  const originPart = parts[0].trim();
  const destPart = parts[1].trim();
  if (!originPart || !destPart) return null;

  const resolveAirport = (part: string): Airport | null => {
    const p = part.toUpperCase();
    if (p.length === 3 && /^[A-Z]{3}$/.test(p)) {
      return findAirport(p);
    }
    return findAirportByCity(part) ?? findAirport(part);
  };

  const origin = resolveAirport(originPart);
  const dest = resolveAirport(destPart);
  if (!origin || !dest || !origin.iata || !dest.iata) return null;
  if (origin.iata === dest.iata) return null;

  const durationMinutes = estimateFlightDurationMinutes(
    origin.iata,
    dest.iata
  );
  if (durationMinutes == null || durationMinutes <= 0) return null;

  return {
    origin,
    dest,
    originIata: origin.iata,
    destIata: dest.iata,
    durationMinutes,
  };
}

/**
 * Calculate great-circle distance between two points using Haversine formula.
 * @returns Distance in kilometers
 */
export function calculateGreatCircleDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

/** Typical cruise speed (km/h) for commercial jets */
const TYPICAL_CRUISE_SPEED_KMH = 850;

/** Buffer minutes for takeoff, landing, taxi */
const GROUND_BUFFER_MINUTES = 60;

/**
 * Estimate flight duration from origin and destination IATA codes.
 * Uses great-circle distance and typical cruise speed.
 * @returns Estimated duration in minutes, or null if either airport not found
 */
export function estimateFlightDurationMinutes(
  originIata: string,
  destinationIata: string
): number | null {
  const origin = findAirport(originIata);
  const dest = findAirport(destinationIata);
  if (!origin || !dest) return null;
  const km = calculateGreatCircleDistance(
    origin.lat,
    origin.lon,
    dest.lat,
    dest.lon
  );
  const flightHours = km / TYPICAL_CRUISE_SPEED_KMH;
  const totalMinutes = Math.round(flightHours * 60 + GROUND_BUFFER_MINUTES);
  return Math.max(0, totalMinutes);
}
